import { TILLAGE_IMPLEMENT, TillageEvent } from '@cibo/core'
import { DatePickerField, NumberInputField, SelectField } from '@cibo/ui'
import ClearIcon from '@mui/icons-material/Clear'
import EditRounded from '@mui/icons-material/EditRounded'
import {
  Button,
  Collapse,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Field, FieldProps, Formik, FormikHelpers, useField } from 'formik'
import numeral from 'numeral'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EventComponentProps } from '../EventDetail/EventDetailTypes'
import { useIsRequired } from '../useIsRequired'
import { TillageEventValidationSchema } from './schema'

export const TillageEventEditorSimple = ({
  editing,
  year,
  name,
  onEditEvent,
  onPressRemove,
  requirement,
  isImmutable,
}: EventComponentProps) => {
  const { t } = useTranslation('@cibo/landmanager/TillageEditor')
  const [{ value }, { initialValue, touched }, { setValue, setTouched }] =
    useField<TillageEvent>(name)
  const [localEditing, setLocalEditing] = useState(!initialValue)
  const isRequired = useIsRequired(requirement)

  useEffect(() => {
    onEditEvent(!initialValue)
  }, [!initialValue])

  const defaultMonth = new Date(year, 3)
  const validationSchema = TillageEventValidationSchema({ t, requirement })

  const handleSubmit = (values: TillageEvent, { setSubmitting }: FormikHelpers<TillageEvent>) => {
    setTouched(true, false)
    setValue(values)
    setLocalEditing(false)
    setSubmitting(false)
    onEditEvent(false)
  }

  const editingDisabled = editing || isImmutable

  return (
    <>
      <Collapse in={!localEditing}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: '275px',
          }}
        >
          <Stack spacing={1}>
            <Typography>{value.implement ? t(value.implement) : '--'}</Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              divider={<Divider orientation="vertical" flexItem />}
            >
              {value.date && (
                <Typography variant="caption">
                  {t('value', { context: 'date', date: new Date(value.date) })}
                </Typography>
              )}
              {value.depth !== undefined && (
                <Typography variant="caption">
                  {t('value', { context: 'depth', depth: value.depth })}
                </Typography>
              )}
              {value.residueRemaining !== undefined && (
                <Typography variant="caption">
                  {t('value', {
                    context: 'residueRemaining',
                    residueRemaining: value.residueRemaining,
                  })}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack direction="column">
            <Tooltip title={t('edit')}>
              <IconButton
                aria-label={t('editEvent') as string}
                onClick={() => {
                  setLocalEditing(true)
                  onEditEvent(true)
                }}
                size="small"
                disabled={editingDisabled}
              >
                <EditRounded />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('remove')}>
              <IconButton
                aria-label={t('removeEvent') as string}
                onClick={onPressRemove}
                size="small"
                disabled={editingDisabled}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Collapse>
      <Formik
        initialValues={value || ({ date: null, implement: '' } as unknown as TillageEvent)}
        validationSchema={validationSchema}
        validateOnMount
        enableReinitialize
        disabled={isImmutable}
        onSubmit={handleSubmit}
      >
        {({ isValid, resetForm, submitForm, values, dirty, errors }) => {
          return (
            <Collapse in={localEditing} timeout="auto" unmountOnExit>
              <Stack spacing={1}>
                <FormControl fullWidth required={isRequired('date')}>
                  <FormLabel>{t('date')}</FormLabel>
                  <DatePickerField
                    name={`date`}
                    minDate={new Date(year - 1, 6, 1)}
                    maxDate={new Date(year + 1, 0, 1)}
                    referenceDate={defaultMonth}
                    data-testid={`date`}
                  />
                </FormControl>

                <FormControl fullWidth required>
                  <FormLabel>{t('implement')}</FormLabel>
                  <SelectField fieldName="implement" renderOption={t} options={TILLAGE_IMPLEMENT} />
                </FormControl>
                <Collapse
                  in={values?.implement === 'stripTill'}
                  sx={values?.implement !== 'stripTill' ? { marginTop: '0 !important' } : {}}
                >
                  <Stack spacing={1}>
                    <FormControl fullWidth required>
                      <FormLabel htmlFor={`rowSpacing`}>{t('rowSpacing')}</FormLabel>
                      <NumberInputField
                        name={`rowSpacing`}
                        min={0}
                        max={100}
                        unit={t('inch')}
                        data-testid={`rowSpacing`}
                      />
                    </FormControl>
                    <FormControl fullWidth required>
                      <FormLabel htmlFor={`stripWidth`}>{t('stripWidth')}</FormLabel>
                      <NumberInputField
                        name={`stripWidth`}
                        min={0}
                        max={100}
                        unit={t('inch')}
                        data-testid={`stripWidth`}
                      />
                    </FormControl>
                  </Stack>
                </Collapse>

                <FormControl fullWidth required={isRequired('depth')}>
                  <FormLabel>{t('depth')}</FormLabel>
                  <NumberInputField
                    name={`depth`}
                    data-testid={`depth`}
                    min={0}
                    max={24}
                    unit={t('inch')}
                  />
                </FormControl>
                <Field name={`residueRemaining`}>
                  {({ field, form }: FieldProps<number>) => (
                    <FormControl fullWidth required={isRequired('residueRemaining')}>
                      <FormLabel>{t('residueRemaining')}</FormLabel>
                      <TextField
                        {...field}
                        type="number"
                        onChange={event => {
                          const newValue = +event.target.value / 100
                          form.setFieldTouched(`residueRemaining`, true)
                          form.setFieldValue(`residueRemaining`, newValue)
                        }}
                        error={!!errors.residueRemaining}
                        helperText={errors.residueRemaining}
                        slotProps={{
                          input: {
                            inputProps: {
                              min: 0,
                              max: 100,
                              'data-testid': `residueRemaining`,
                            },
                            endAdornment: <InputAdornment position="end">{'%'}</InputAdornment>,
                          },
                        }}
                        value={!!field.value ? numeral(field.value * 100).format('0,0') : ''}
                      />
                    </FormControl>
                  )}
                </Field>
                <Stack direction="row" spacing={1} justifyContent="flex-end" pb={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    data-testid={`${name}-cancel`}
                    onClick={() => {
                      !initialValue && !touched && onPressRemove()
                      setLocalEditing(false)
                      onEditEvent(false)
                      resetForm()
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    data-testid={`${name}-add`}
                    size="small"
                    onClick={() => submitForm()}
                    disabled={!isValid || !dirty}
                  >
                    {!initialValue ? t('add') : t('saveEvent')}
                  </Button>
                </Stack>
              </Stack>
            </Collapse>
          )
        }}
      </Formik>
    </>
  )
}
